/**
 * Blog Page - List all blog posts with filtering
 * Displays all published blog posts with category/tag filtering and search
 */

import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiFilter, FiRss } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import useEditableContent from '../hooks/useEditableContent';
import ContentCard from '../components/ContentCard';
import ContentGrid from '../components/ContentGrid';
import BlogEditModal from '../components/BlogEditModal';
import ConfirmModal from '../components/ConfirmModal';
import SEO from '../components/SEO';
import api from '../services/api';
import { useToast } from '../context/ToastContext';

const Blog = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [showFeedMenu, setShowFeedMenu] = useState(false);
  const feedMenuRef = useRef(null);
  const hasHandledNavigationRef = useRef(false);

  // Use unified hook for blog content - show all if admin, only published if not
  const {
    items: blogPosts,
    loading,
    showModal,
    isCreating,
    formData,
    updateFormData,
    startCreate,
    startEdit,
    cancelEdit,
    save,
    deleteItem,
    confirmDelete,
    confirmOpen,
    itemToDelete,
    setConfirmOpen,
    fetchItems
  } = useEditableContent('blog', isAdmin ? {} : { published: true });

  // Check if we should auto-open edit modal for a specific post (from Home page navigation)
  useEffect(() => {
    if (location.state?.editPostId && blogPosts.length > 0 && !hasHandledNavigationRef.current) {
      const postToEdit = blogPosts.find(post => post._id === location.state.editPostId);
      if (postToEdit) {
        startEdit(postToEdit);
        // Mark as handled and clear the state so it doesn't re-trigger
        hasHandledNavigationRef.current = true;
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, blogPosts, startEdit]);

  // Extract unique categories and tags
  const categories = ['all', ...new Set(blogPosts.map(post => post.category).filter(Boolean))];
  const allTags = [...new Set(blogPosts.flatMap(post => post.data?.tags || []).filter(Boolean))];
  const tags = ['all', ...allTags];

  // Filter posts based on search, category, and tag
  useEffect(() => {
    let filtered = [...blogPosts];

    // Search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(post =>
        post.title?.toLowerCase().includes(lowerSearch) ||
        post.shortDescription?.toLowerCase().includes(lowerSearch) ||
        post.data?.excerpt?.toLowerCase().includes(lowerSearch)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Tag filter
    if (selectedTag !== 'all') {
      filtered = filtered.filter(post => post.data?.tags?.includes(selectedTag));
    }

    setFilteredPosts(filtered);
  }, [blogPosts, searchTerm, selectedCategory, selectedTag]);

  // Close feed menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (feedMenuRef.current && !feedMenuRef.current.contains(event.target)) {
        setShowFeedMenu(false);
      }
    };

    if (showFeedMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFeedMenu]);

  const handleCreateBlog = () => {
    startCreate({
      contentType: 'blog',
      category: 'Technology',
      data: {
        author: 'Richard Plotka',
        date: new Date(),
        readTime: 5,
        tags: [],
        format: 'html'
      },
      metadata: {
        published: true,
        page: 'blog',
        section: 'posts'
      }
    });
  };

  // Toggle publish status
  const handleTogglePublish = async (post) => {
    try {
      const updatedMetadata = {
        ...post.metadata,
        published: post.metadata?.published === false ? true : false
      };
      
      await api.put(`/content-items/${post._id}`, {
        ...post,
        metadata: updatedMetadata
      });
      
      // Refresh the list to show updated status
      await fetchItems();
      
      showToast(
        updatedMetadata.published ? 'Post published' : 'Post set as draft',
        'success'
      );
      return true;
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
      showToast('Failed to update publish status', 'error');
      return false;
    }
  };

  // Toggle featured/highlighted status
  const handleToggleHighlight = async (post) => {
    try {
      const isCurrentlyHighlighted = post.metadata?.page === 'home' && post.metadata?.section === 'blog';
      const updatedMetadata = {
        ...post.metadata,
        page: isCurrentlyHighlighted ? 'blog' : 'home',
        section: isCurrentlyHighlighted ? 'posts' : 'blog'
      };
      
      await api.put(`/content-items/${post._id}`, {
        ...post,
        metadata: updatedMetadata
      });
      
      // Refresh the list to show updated status
      await fetchItems();
      
      showToast(
        isCurrentlyHighlighted ? 'Removed from featured' : 'Added to featured',
        'success'
      );
      return true;
    } catch (error) {
      console.error('Failed to toggle highlight status:', error);
      showToast('Failed to update featured status', 'error');
      return false;
    }
  };

  if (loading) {
    return (
      <Container>
        <ContentWrapper>
          <LoadingMessage>Loading blog posts...</LoadingMessage>
        </ContentWrapper>
      </Container>
    );
  }

  return (
    <>
      <SEO
        title="Blog"
        description="Read insights on technology, innovation, and web development from Richard Plotka. Explore articles, tutorials, and industry perspectives."
        keywords={['Richard Plotka', 'Blog', 'Technology', 'Innovation', 'Web Development', 'Articles']}
      />
      <Container>
        <ContentWrapper>
          {/* Header */}
          <Header>
          <HeaderContent>
            <Title>Blog</Title>
            <Subtitle>Insights on technology, innovation, and web development</Subtitle>
          </HeaderContent>
          <HeaderActions>
            <FeedSubscribe ref={feedMenuRef}>
              <SubscribeButton 
                onClick={() => setShowFeedMenu(!showFeedMenu)}
                as="button"
                title="Subscribe to blog updates"
              >
                <FiRss /> Subscribe
              </SubscribeButton>
              {showFeedMenu && (
                <FeedMenu>
                  <FeedMenuItem 
                    href={`${process.env.REACT_APP_API_BASE || 'http://localhost:5001'}/api/feed/rss`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FiRss /> RSS Feed
                  </FeedMenuItem>
                  <FeedMenuItem 
                    href={`${process.env.REACT_APP_API_BASE || 'http://localhost:5001'}/api/feed/atom`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FiRss /> Atom Feed
                  </FeedMenuItem>
                </FeedMenu>
              )}
            </FeedSubscribe>
            {isAdmin && (
              <CreateButton onClick={handleCreateBlog}>
                <FiPlus /> New Post
              </CreateButton>
            )}
          </HeaderActions>
        </Header>

        {/* Search and Filters */}
        <FiltersSection>
          <SearchBar>
            <FiSearch />
            <SearchInput
              type="text"
              placeholder="Search blog posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>

          <FilterControls>
            <FilterGroup>
              <FilterLabel>
                <FiFilter /> Category:
              </FilterLabel>
              <FilterSelect
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </option>
                ))}
              </FilterSelect>
            </FilterGroup>

            {tags.length > 1 && (
              <FilterGroup>
                <FilterLabel>Tag:</FilterLabel>
                <FilterSelect
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                >
                  {tags.map(tag => (
                    <option key={tag} value={tag}>
                      {tag === 'all' ? 'All Tags' : tag}
                    </option>
                  ))}
                </FilterSelect>
              </FilterGroup>
            )}
          </FilterControls>
        </FiltersSection>

        {/* Results count */}
        <ResultsCount>
          {filteredPosts.length} {filteredPosts.length === 1 ? 'post' : 'posts'}
        </ResultsCount>

        {/* Blog posts grid */}
        {filteredPosts.length > 0 ? (
          <ContentGrid>
            <AnimatePresence mode="popLayout">
              {filteredPosts.map((post, index) => (
                <BlogCardWrapper
                  key={post._id}
                  as={motion.div}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  {isAdmin && (
                    <CardAdminControls>
                      <EditIconButton 
                        onClick={(e) => {
                          e.preventDefault();
                          startEdit(post);
                        }}
                        title="Edit post"
                      >
                        <FiEdit2 />
                      </EditIconButton>
                      <DeleteIconButton 
                        onClick={(e) => {
                          e.preventDefault();
                          confirmDelete(post);
                        }}
                        title="Delete post"
                      >
                        <FiTrash2 />
                      </DeleteIconButton>
                    </CardAdminControls>
                  )}
                  <ContentCard
                    to={`/blog/${post._id}`}
                    category={post.category}
                    title={post.title}
                    excerpt={post.data?.excerpt || post.shortDescription}
                    status={isAdmin ? {
                      published: post.metadata?.published !== false,
                      highlighted: post.metadata?.page === 'home' && post.metadata?.section === 'blog'
                    } : undefined}
                    onTogglePublish={isAdmin ? () => handleTogglePublish(post) : undefined}
                    onToggleHighlight={isAdmin ? () => handleToggleHighlight(post) : undefined}
                    metadata={{
                      readTime: post.data?.readTime || 3,
                      tags: post.data?.tags || [],
                      date: post.data?.date || post.createdAt
                    }}
                    index={0}
                  />
                </BlogCardWrapper>
              ))}
            </AnimatePresence>
          </ContentGrid>
        ) : (
          <EmptyState>
            <EmptyIcon>📝</EmptyIcon>
            <EmptyTitle>No blog posts found</EmptyTitle>
            <EmptyText>
              {searchTerm || selectedCategory !== 'all' || selectedTag !== 'all'
                ? 'Try adjusting your filters or search term'
                : isAdmin
                ? 'Create your first blog post to get started'
                : 'Check back soon for new content'}
            </EmptyText>
          </EmptyState>
        )}

        {/* Create/Edit Modal */}
        <BlogEditModal
          isOpen={showModal}
          isCreating={isCreating}
          onClose={cancelEdit}
          onSave={save}
          formData={formData}
          updateFormData={updateFormData}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            deleteItem(itemToDelete);
          }}
          title="Delete Blog Post"
          message="Are you sure you want to delete this blog post? This action cannot be undone."
        />
      </ContentWrapper>
    </Container>
    </>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  padding: 100px 2rem 4rem;
  background: var(--bg-primary);
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 3rem;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 5vw, 4rem);
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--accent-gradient);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 122, 255, 0.3);
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
`;

const SubscribeButton = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: var(--accent);
  border: 2px solid var(--accent);
  padding: 0.875rem 1.75rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
  white-space: nowrap;

  svg {
    font-size: 1.1rem;
  }

  &:hover {
    background: var(--accent);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const FeedSubscribe = styled.div`
  position: relative;
`;

const FeedMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  background: var(--bg-secondary);
  border: 2px solid var(--accent);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  min-width: 200px;
  overflow: hidden;
  z-index: 1000;
  animation: slideDown 0.2s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FeedMenuItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  color: var(--text-primary);
  text-decoration: none;
  transition: background 0.2s ease;
  font-weight: 500;

  svg {
    color: var(--accent);
    font-size: 1.1rem;
  }

  &:hover {
    background: var(--accent);
    color: white;

    svg {
      color: white;
    }
  }

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;


const FiltersSection = styled.div`
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--bg-secondary);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  border: 2px solid transparent;
  transition: border-color 0.3s ease;

  &:focus-within {
    border-color: var(--accent);
  }

  svg {
    color: var(--text-secondary);
    font-size: 1.2rem;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--text-primary);
  font-size: 1rem;

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const FilterControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;

  svg {
    font-size: 1rem;
  }
`;

const FilterSelect = styled.select`
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 2px solid rgba(255, 255, 255, 0.1);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: var(--accent);
  }

  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`;

const ResultsCount = styled.div`
  color: var(--text-secondary);
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const EmptyText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-secondary);
  font-size: 1.2rem;
`;

const CardAdminControls = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 10;
`;

const EditIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(102, 126, 234, 0.1);
  border: 1px solid var(--accent-primary);
  color: var(--accent-primary);
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--accent-primary);
    color: white;
    transform: scale(1.05);
  }
`;

const DeleteIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #ef4444;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #ef4444;
    color: white;
    transform: scale(1.05);
  }
`;

const BlogCardWrapper = styled.div`
  position: relative;
  
  &:hover ${CardAdminControls} {
    opacity: 1;
  }
`;

export default Blog;
